import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "../../lib/smooth";

const LINKS = [
  { id: "programma", label: "Programma" },
  { id: "come-funziona", label: "Come funziona" },
  { id: "contatti-qualificati", label: "Contatti qualificati" },
  { id: "settori", label: "Settori" },
  { id: "casi-studio", label: "Casi studio", cases: true },
];

export const Navbar = ({ hasCases = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const links = LINKS.filter((l) => !l.cases || hasCases);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (pathname === "/") scrollToId(id);
    else navigate(`/#${id}`);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 px-3 sm:px-5 pt-3">
      <nav
        data-testid="main-navbar"
        className={`mx-auto max-w-7xl flex items-center justify-between rounded-2xl px-4 sm:px-5 h-14 transition-[background-color,border-color,box-shadow] duration-500 border ${scrolled || open ? "glass shadow-2xl shadow-black/40" : "border-transparent"}`}
      >
        <Link to="/" data-testid="nav-logo" aria-label="TERMONLINE home"><Logo /></Link>
        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <button key={l.id} data-testid={`nav-link-${l.id}`} onClick={() => go(l.id)}
              className="px-3.5 py-2 text-sm text-slate-300 hover:text-white rounded-full hover:bg-white/5 transition-colors">
              {l.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="nav-cta-consulenza" onClick={() => go("consulenza")}
            className="hidden sm:inline-flex group items-center gap-1.5 rounded-full bg-[#ff5500] hover:bg-[#ff6a1f] text-white text-sm font-medium pl-4 pr-3 h-10 transition-colors">
            Richiedi una consulenza
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button data-testid="nav-mobile-toggle" aria-label="Menu" onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 grid place-items-center rounded-full border hairline text-white">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div data-testid="nav-mobile-menu" initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }} className="lg:hidden mx-auto max-w-7xl mt-2 glass rounded-2xl p-3">
            {links.map((l, i) => (
              <motion.button key={l.id} data-testid={`nav-mobile-link-${l.id}`} onClick={() => go(l.id)}
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i }}
                className="w-full flex items-center justify-between px-3 py-3.5 text-left font-display text-xl font-bold text-white border-b hairline last:border-0">
                {l.label}<span className="font-mono text-xs text-slate-400">0{i + 1}</span>
              </motion.button>
            ))}
            <button data-testid="nav-mobile-cta" onClick={() => go("consulenza")}
              className="mt-3 w-full h-12 rounded-xl bg-[#ff5500] text-white font-medium">Richiedi una consulenza</button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
